import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CalculatorIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

// Constants
const TOTAL_STUDENTS_AU = 4_100_000;
const PRIVATE_SCHOOLS = 3_200;
const PUBLIC_SCHOOLS = 6_500;
const TOTAL_SCHOOLS = PRIVATE_SCHOOLS + PUBLIC_SCHOOLS;
const AVG_STUDENTS_PER_SCHOOL = Math.round(TOTAL_STUDENTS_AU / TOTAL_SCHOOLS); // ~423

const InvSlide13Economics: React.FC = () => {
  // Adjustable parameters
  const [annualPrice, setAnnualPrice] = useState(80); // $ per student per year
  const [operatingCost, setOperatingCost] = useState(3); // $ per student per year
  const [acquisitionCost, setAcquisitionCost] = useState(15); // $ per student
  const [schoolCount, setSchoolCount] = useState(50); // Number of schools
  const [studentsPerSchool, setStudentsPerSchool] = useState(AVG_STUDENTS_PER_SCHOOL);

  // Calculations
  const calculations = useMemo(() => {
    const totalStudents = schoolCount * studentsPerSchool;
    const annualRevenue = totalStudents * annualPrice;
    const annualOperatingCost = totalStudents * operatingCost;
    const totalAcquisitionCost = totalStudents * acquisitionCost;
    const grossProfit = annualRevenue - annualOperatingCost;
    const grossMargin = annualRevenue > 0 ? (grossProfit / annualRevenue) * 100 : 0;
    const netFirstYear = grossProfit - totalAcquisitionCost;
    const marketPenetration = (totalStudents / TOTAL_STUDENTS_AU) * 100;
    const schoolPenetration = (schoolCount / TOTAL_SCHOOLS) * 100;

    // LTV calculations (assume 3 year average retention)
    const avgRetentionYears = 3;
    const ltv = annualPrice * avgRetentionYears;
    const ltvToCac = acquisitionCost > 0 ? ltv / acquisitionCost : 0;

    // Break-even calculations
    const monthsToBreakeven = acquisitionCost > 0
      ? Math.ceil(acquisitionCost / ((annualPrice - operatingCost) / 12))
      : 0;

    return {
      totalStudents,
      annualRevenue,
      annualOperatingCost,
      totalAcquisitionCost,
      grossProfit,
      grossMargin,
      netFirstYear,
      marketPenetration,
      schoolPenetration,
      ltv,
      ltvToCac,
      monthsToBreakeven,
    };
  }, [annualPrice, operatingCost, acquisitionCost, schoolCount, studentsPerSchool]);

  // Milestone scenarios
  const milestones = [
    { label: '10 schools', schools: 10, students: 10 * studentsPerSchool },
    { label: '50 schools', schools: 50, students: 50 * studentsPerSchool },
    { label: '100 schools', schools: 100, students: 100 * studentsPerSchool },
    { label: '500 schools', schools: 500, students: 500 * studentsPerSchool },
    { label: '1% market', schools: Math.round(TOTAL_SCHOOLS * 0.01), students: Math.round(TOTAL_STUDENTS_AU * 0.01) },
    { label: '5% market', schools: Math.round(TOTAL_SCHOOLS * 0.05), students: Math.round(TOTAL_STUDENTS_AU * 0.05) },
  ];

  const formatCurrency = (value: number) => {
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    } else if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatNumber = (value: number) => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(2)}M`;
    } else if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  return (
    <div className="flex flex-col h-full px-10 py-6 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-4"
      >
        <h2 className="text-4xl font-bold text-white mb-1 flex items-center justify-center gap-3">
          <CalculatorIcon className="w-10 h-10 text-[#0ea5e9]" />
          Unit Economics Model
        </h2>
        <p className="text-lg text-[#94a3b8]">
          Adjust parameters to explore scenarios • {formatNumber(TOTAL_STUDENTS_AU)} students across {formatNumber(TOTAL_SCHOOLS)} schools
        </p>
      </motion.div>

      <div className="flex gap-6 flex-1 min-h-0">
        {/* Left: Controls */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="w-72 flex-shrink-0"
        >
          <div
            className="p-4 rounded-xl h-full overflow-y-auto"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <h3 className="text-sm font-semibold text-[#94a3b8] mb-4 flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              PARAMETERS
            </h3>

            {/* Annual Price */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#94a3b8]">Annual Price</span>
                <span className="text-[#10b981] font-bold">${annualPrice}/student</span>
              </div>
              <input
                type="range"
                min="30"
                max="360"
                value={annualPrice}
                onChange={(e) => setAnnualPrice(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #10b981 ${(annualPrice - 30) / 3.3}%, #334155 ${(annualPrice - 30) / 3.3}%)` }}
              />
              <div className="flex justify-between text-xs text-[#64748b]">
                <span>$30</span>
                <span>$360</span>
              </div>
            </div>

            {/* Operating Cost */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#94a3b8]">Operating Cost</span>
                <span className="text-[#ef4444] font-bold">${operatingCost}/student</span>
              </div>
              <input
                type="range"
                min="1"
                max="20"
                step="0.5"
                value={operatingCost}
                onChange={(e) => setOperatingCost(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #ef4444 ${(operatingCost - 1) / 0.19}%, #334155 ${(operatingCost - 1) / 0.19}%)` }}
              />
              <div className="flex justify-between text-xs text-[#64748b]">
                <span>$1</span>
                <span>$20</span>
              </div>
            </div>

            {/* Acquisition Cost */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#94a3b8]">Acquisition Cost</span>
                <span className="text-[#f59e0b] font-bold">${acquisitionCost}/student</span>
              </div>
              <input
                type="range"
                min="0"
                max="150"
                value={acquisitionCost}
                onChange={(e) => setAcquisitionCost(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #f59e0b ${acquisitionCost / 1.5}%, #334155 ${acquisitionCost / 1.5}%)` }}
              />
              <div className="flex justify-between text-xs text-[#64748b]">
                <span>$0</span>
                <span>$150</span>
              </div>
            </div>

            <div className="h-px bg-[#334155] my-4" />

            {/* Number of Schools */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#94a3b8]">Schools</span>
                <span className="text-[#0ea5e9] font-bold">{schoolCount}</span>
              </div>
              <input
                type="range"
                min="1"
                max="1000"
                value={schoolCount}
                onChange={(e) => setSchoolCount(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #0ea5e9 ${schoolCount / 10}%, #334155 ${schoolCount / 10}%)` }}
              />
              <div className="flex justify-between text-xs text-[#64748b]">
                <span>1</span>
                <span>1,000</span>
              </div>
            </div>

            {/* Students per School */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#94a3b8]">Students/School</span>
                <span className="text-[#8b5cf6] font-bold">{studentsPerSchool}</span>
              </div>
              <input
                type="range"
                min="100"
                max="1200"
                step="50"
                value={studentsPerSchool}
                onChange={(e) => setStudentsPerSchool(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #8b5cf6 ${(studentsPerSchool - 100) / 11}%, #334155 ${(studentsPerSchool - 100) / 11}%)` }}
              />
              <div className="flex justify-between text-xs text-[#64748b]">
                <span>100</span>
                <span>1200</span>
              </div>
            </div>

            <div className="h-px bg-[#334155] my-4" />

            {/* Quick scenarios */}
            <h4 className="text-xs font-semibold text-[#64748b] mb-2">QUICK SCENARIOS</h4>
            <div className="grid grid-cols-2 gap-2">
              {milestones.slice(0, 4).map((m) => (
                <button
                  key={m.label}
                  onClick={() => setSchoolCount(m.schools)}
                  className={`px-2 py-1.5 rounded text-xs transition-all ${
                    schoolCount === m.schools
                      ? 'bg-[#0ea5e9] text-white'
                      : 'bg-[#1e293b] text-[#94a3b8] hover:bg-[#334155]'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Middle: Key Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <div className="grid grid-cols-3 gap-3 mb-4">
            {/* Total Students */}
            <div
              className="p-4 rounded-xl text-center"
              style={{
                background: 'rgba(14, 165, 233, 0.1)',
                border: '1px solid rgba(14, 165, 233, 0.3)',
              }}
            >
              <div className="text-3xl font-bold text-[#0ea5e9]">
                {formatNumber(calculations.totalStudents)}
              </div>
              <div className="text-sm text-[#94a3b8]">Total Students</div>
              <div className="text-xs text-[#64748b] mt-1">
                {calculations.marketPenetration.toFixed(2)}% of market
              </div>
            </div>

            {/* Annual Revenue */}
            <div
              className="p-4 rounded-xl text-center"
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
              }}
            >
              <div className="text-3xl font-bold text-[#10b981]">
                {formatCurrency(calculations.annualRevenue)}
              </div>
              <div className="text-sm text-[#94a3b8]">Annual Revenue</div>
              <div className="text-xs text-[#64748b] mt-1">
                {formatCurrency(calculations.annualRevenue / 12)}/month
              </div>
            </div>

            {/* Gross Margin */}
            <div
              className="p-4 rounded-xl text-center"
              style={{
                background: calculations.grossMargin >= 80
                  ? 'rgba(16, 185, 129, 0.15)'
                  : calculations.grossMargin >= 50
                  ? 'rgba(245, 158, 11, 0.15)'
                  : 'rgba(239, 68, 68, 0.15)',
                border: calculations.grossMargin >= 80
                  ? '2px solid rgba(16, 185, 129, 0.5)'
                  : calculations.grossMargin >= 50
                  ? '2px solid rgba(245, 158, 11, 0.5)'
                  : '2px solid rgba(239, 68, 68, 0.5)',
              }}
            >
              <div
                className="text-3xl font-bold"
                style={{
                  color: calculations.grossMargin >= 80
                    ? '#10b981'
                    : calculations.grossMargin >= 50
                    ? '#f59e0b'
                    : '#ef4444',
                }}
              >
                {calculations.grossMargin.toFixed(1)}%
              </div>
              <div className="text-sm text-[#94a3b8]">Gross Margin</div>
              <div className="text-xs text-[#64748b] mt-1">
                {formatCurrency(calculations.grossProfit)} profit
              </div>
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div
              className="p-3 rounded-lg text-center"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <div className="text-xl font-bold text-white">{formatCurrency(calculations.annualOperatingCost)}</div>
              <div className="text-xs text-[#64748b]">Operating Costs</div>
            </div>
            <div
              className="p-3 rounded-lg text-center"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <div className="text-xl font-bold text-white">{formatCurrency(calculations.totalAcquisitionCost)}</div>
              <div className="text-xs text-[#64748b]">Total CAC</div>
            </div>
            <div
              className="p-3 rounded-lg text-center"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <div className="text-xl font-bold text-[#10b981]">{formatCurrency(calculations.ltv)}</div>
              <div className="text-xs text-[#64748b]">3yr LTV</div>
            </div>
            <div
              className="p-3 rounded-lg text-center"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <div className="text-xl font-bold text-[#0ea5e9]">{calculations.ltvToCac.toFixed(1)}x</div>
              <div className="text-xs text-[#64748b]">LTV:CAC</div>
            </div>
          </div>

          {/* P&L Summary */}
          <div
            className="p-4 rounded-xl mb-4"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <h4 className="text-sm font-semibold text-[#94a3b8] mb-3">FIRST YEAR P&L</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Revenue</span>
                <span className="text-[#10b981] font-medium">{formatCurrency(calculations.annualRevenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Operating Costs</span>
                <span className="text-[#ef4444] font-medium">-{formatCurrency(calculations.annualOperatingCost)}</span>
              </div>
              <div className="flex justify-between border-t border-[#334155] pt-2">
                <span className="text-white font-medium">Gross Profit</span>
                <span className="text-[#10b981] font-bold">{formatCurrency(calculations.grossProfit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Acquisition Costs</span>
                <span className="text-[#f59e0b] font-medium">-{formatCurrency(calculations.totalAcquisitionCost)}</span>
              </div>
              <div className="flex justify-between border-t border-[#334155] pt-2">
                <span className="text-white font-medium">Net Year 1</span>
                <span className={`font-bold ${calculations.netFirstYear >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                  {formatCurrency(calculations.netFirstYear)}
                </span>
              </div>
            </div>
            {calculations.monthsToBreakeven > 0 && calculations.monthsToBreakeven <= 12 && (
              <div className="mt-3 pt-3 border-t border-[#334155] text-center">
                <span className="text-sm text-[#94a3b8]">CAC Payback: </span>
                <span className="text-sm text-[#0ea5e9] font-bold">{calculations.monthsToBreakeven} months</span>
              </div>
            )}
          </div>

          {/* Year 2+ (no CAC) */}
          <div
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(14, 165, 233, 0.15) 100%)',
              border: '2px solid rgba(16, 185, 129, 0.4)',
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-[#94a3b8]">Year 2+ Net Profit (no CAC)</div>
                <div className="text-2xl font-bold text-[#10b981]">{formatCurrency(calculations.grossProfit)}/year</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-[#94a3b8]">Per School</div>
                <div className="text-xl font-bold text-white">
                  {formatCurrency(calculations.grossProfit / schoolCount)}/yr
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right: Milestone Projections */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="w-64 flex-shrink-0"
        >
          <div
            className="p-4 rounded-xl h-full"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <h3 className="text-sm font-semibold text-[#94a3b8] mb-4">MILESTONE PROJECTIONS</h3>

            <div className="space-y-3">
              {milestones.map((m) => {
                const revenue = m.students * annualPrice;
                const profit = m.students * (annualPrice - operatingCost);
                return (
                  <button
                    key={m.label}
                    onClick={() => setSchoolCount(m.schools)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      schoolCount === m.schools
                        ? 'bg-[#0ea5e9]/20 border border-[#0ea5e9]'
                        : 'bg-[#1e293b]/50 border border-transparent hover:border-[#334155]'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-white font-medium">{m.label}</span>
                      <span className="text-xs text-[#64748b]">{formatNumber(m.students)} students</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#94a3b8]">Revenue</span>
                      <span className="text-[#10b981]">{formatCurrency(revenue)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#94a3b8]">Gross Profit</span>
                      <span className="text-[#0ea5e9]">{formatCurrency(profit)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-[#334155]">
              <div className="text-xs text-[#64748b] text-center">
                Market: {formatNumber(TOTAL_STUDENTS_AU)} students
                <br />
                {formatNumber(PRIVATE_SCHOOLS)} private + {formatNumber(PUBLIC_SCHOOLS)} public schools
                <br />
                ~{AVG_STUDENTS_PER_SCHOOL} avg students/school
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvSlide13Economics;
